import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, isDevMode } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { catchError, interval, of, switchMap, timeout } from 'rxjs';
import { LocaleService } from './locale.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class SWService {
  constructor(
    private updates: SwUpdate,
    // private swPush: SwPush,
    private toast: ToastService,
    private destroyRef: DestroyRef,
    private localeService: LocaleService,
    private http: HttpClient,
  ) {}

  init() {
    //开发模式关闭
    if (isDevMode()) return;

    // 应用启动时检查版本一致性（只检查一次）
    this.checkAndCleanupIfNeeded();

    // 每隔10分钟检查一次是否有更新
    interval(10 * 60 * 1000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() =>
          this.http.get<{ version: string } | null>(window.location.origin + '/release.json?v=' + Date.now()).pipe(
            timeout(10 * 1000),
            catchError(() => of(null)),
          ),
        ),
      )
      .subscribe(v => {
        if (v?.version !== window.version) {
          try {
            console.log('versionUpdates:', 'checkForUpdate...');
            this.updates.checkForUpdate();
          } catch (err) {
            console.error('Failed to check for updates:', err);
          }
        }
      });

    // 提示刷新
    this.updates.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(e => {
      switch (e.type) {
        case 'VERSION_DETECTED':
          //每当在服务器上检测到新版本时发出事件
          console.log('versionUpdates:', 'VERSION_DETECTED');
          break;
        case 'VERSION_READY':
          //每当新版本下载并准备好激活时发出一个事件
          console.log('versionUpdates:', 'VERSION_READY');

          // 清理旧的 Service Worker 缓存
          this.cleanupOldCaches();

          // this.toast.show({
          //   title: '',
          //   type: 'success',
          //   message: this.localeService.getValue('new_version_resouce_update'),
          //   duration: 0,
          // });
          break;
        case 'VERSION_INSTALLATION_FAILED':
          //每当检查或下载新版本失败时发出事件
          console.log('versionUpdates:', 'VERSION_INSTALLATION_FAILED');
          break;
        default:
          //无更新
          console.log('versionUpdates:', 'NO_NEW_VERSION_DETECTED');
          break;
      }
    });

    // 提示必须刷新
    this.updates.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(e => {
      // 每当服务工作线程用于为该客户端提供服务的应用程序版本处于损坏状态（如果不重新加载整个页面就无法恢复）时，就会发出一个事件
      console.log('versionUpdates:', 'UNRECOVERABLE');
      this.toast.show({
        title: '',
        type: 'fail',
        message: this.localeService.getValue('version_resouce_break'),
        duration: 0,
      });
    });

    // 注册通知
    // this.swPush.subscription.pipe(first()).subscribe(subscription => {
    //   if (subscription) {
    //     console.log('通知已经注册，swPush', subscription);
    //   } else {
    //     this.swPush
    //       .requestSubscription({
    //         // TODO: 后期应该放到环境变量里面
    //         serverPublicKey: 'BJWKRuUyeZY0JZHUzrZ3z3duB5_lm-5SNfecfbPOusMMSmYVPu8nhzW1G4Ea_OPECw4bPn_i08tUuqpom3SqJwg',
    //       })
    //       .then(sub => {
    //         // TODO: 这里之后需要后端提供订阅接口，把 sub 对象 POST 给后端存起来
    //         console.log('通知注册成功，swPush', sub);
    //         this.notification('提示', '通知注册成功');
    //       })
    //       .catch(_ => console.error(_));
    //   }
    // });

    // 提示用户安装web应用
    // fromEvent(window, 'beforeinstallprompt')
    //   .pipe(delay(2000), first())
    //   .subscribe(install => {
    //     console.log(install);
    //     install.preventDefault();
    //     this.toast.show({
    //       message: '点我！马上安装到桌面，访问快人一步！',
    //       onClick: () => {
    //         //@ts-ignore
    //         install.prompt();
    //       },
    //     });
    //     //@ts-ignore
    //     install.userChoice.then(result => {
    //       if (result.outcome === 'accepted') {
    //         //用户同意了安装
    //         this.toast.show({ type: 'success', message: '安装完成' });
    //       } else {
    //         //用户拒绝了安装
    //       }
    //     });
    //   });
  }

  /**
   * 检查版本并在必要时清理缓存
   * 只在应用启动时执行一次，避免重复检查
   */
  private async checkAndCleanupIfNeeded() {
    try {
      const currentVersion = window.version; // 当前应用版本
      const checkKey = `sw_version_checked_${currentVersion}`;

      // 检查是否已经为这个版本执行过清理
      if (localStorage.getItem(checkKey)) {
        console.log('当前版本已检查过，跳过');
        return;
      }

      console.log('检查缓存版本一致性，当前版本:', currentVersion);

      const cacheNames = await caches.keys();
      const ngswCaches = cacheNames.filter(name => name.startsWith('ngsw:'));

      if (ngswCaches.length === 0) {
        console.log('没有找到缓存');
        localStorage.setItem(checkKey, 'true');
        return;
      }

      // 尝试从控制数据库读取缓存版本
      let cachedVersion: string | null = null;

      for (const cacheName of ngswCaches) {
        if (cacheName.includes(':db:control')) {
          try {
            const cache = await caches.open(cacheName);
            const keys = await cache.keys();

            // 查找 ngsw.json 的缓存
            for (const request of keys) {
              if (request.url.includes('/ngsw.json')) {
                const response = await cache.match(request);
                if (response) {
                  const data = await response.json();
                  cachedVersion = data.timestamp?.toString() || data.configVersion?.toString();
                  console.log('缓存中的版本标识:', cachedVersion);
                  break;
                }
              }
            }
            if (cachedVersion) break;
          } catch (e) {
            console.log('读取缓存版本失败:', e);
          }
        }
      }

      // 判断是否需要清理：1. 找不到版本信息（降级清理） 2. 版本不一致
      const needCleanup = !cachedVersion || cachedVersion !== currentVersion;

      if (needCleanup) {
        console.log(`版本不一致 (缓存:${cachedVersion}, 当前:${currentVersion})，开始清理...`);

        let deletedCount = 0;
        const cachesToDelete = ngswCaches.filter(name => !name.includes(':db:'));

        for (const cacheName of cachesToDelete) {
          const deleted = await caches.delete(cacheName);
          if (deleted) {
            deletedCount++;
            console.log('已删除旧缓存:', cacheName);
          }
        }

        // 清除所有旧版本的检查标记
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sw_version_checked_') && key !== checkKey) {
            localStorage.removeItem(key);
            console.log('已清除旧版本标记:', key);
          }
        });

        console.log(`清理完成，共删除 ${deletedCount} 个旧缓存`);
      } else {
        console.log('缓存版本一致或无法判断，无需清理');
      }

      // 标记已检查
      localStorage.setItem(checkKey, 'true');
    } catch (error) {
      console.error('版本检查失败:', error);
    }
  }

  /**
   * 清理旧的 Service Worker 缓存
   * 在检测到新版本时调用（VERSION_READY）
   */
  private async cleanupOldCaches() {
    try {
      console.log('新版本准备就绪，清理旧缓存...');

      const cacheNames = await caches.keys();
      const ngswCaches = cacheNames.filter(name => name.startsWith('ngsw:') && !name.includes(':db:'));

      let deletedCount = 0;

      for (const cacheName of ngswCaches) {
        const deleted = await caches.delete(cacheName);
        if (deleted) {
          deletedCount++;
          console.log('已删除旧缓存:', cacheName);
        }
      }

      if (deletedCount > 0) {
        console.log(`缓存清理完成，共删除 ${deletedCount} 个旧缓存`);

        // 清除所有版本检查标记，下次启动重新检查
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sw_version_checked_')) {
            localStorage.removeItem(key);
          }
        });
      } else {
        console.log('没有需要清理的旧缓存');
      }
    } catch (error) {
      console.error('清理缓存失败:', error);
    }
  }

  /**
   * 发送系统级别的通知
   *
   * @param title
   * @param msg
   * @param callback
   */
  // notification(title: string, msg: string, callback: () => void = () => {}) {
  //   if ('Notification' in window) {
  //     Notification.requestPermission().then(permission => {
  //       if (permission === 'granted') {
  //         const notification = new Notification(title, {
  //           body: msg,
  //         });

  //         notification.onclick = () => {
  //           // 处理点击通知后的操作
  //           callback();
  //         };
  //       }
  //     });
  //   }
  // }
}
